import { useEffect, useMemo, useState } from "react";
import { findRegimen, regimenBadge, type Regimen } from "@/data/meal-plan";
import type { DayOfWeek, DayPlan, MealSelections, WeekPlan } from "@/types/meal-plan-state";
import { DAYS_OF_WEEK } from "@/types/meal-plan-state";
import { fetchWeekSnapshot } from "@/lib/api";
import { ownerScope } from "@/lib/week-owner";
import { useWeekPlan } from "@/context/week-plan-context";
import { migrateWeekPlan } from "@/lib/migrate-plan";
import { getMealChoices, getMealMenu } from "@/lib/random-plan";
import { addWeeks, formatWeekRange } from "@/lib/week-utils";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { eatenLossWarning, hasEatenMarks } from "@/lib/owner-eaten";

interface DuplicatePreviousWeekDialogProps {
  currentWeekId: string;
  currentWeekPlan: WeekPlan;
  onDuplicate: (
    selections: Record<DayOfWeek, { sourcePlan: DayPlan; mealIndices: number[] } | null>,
  ) => void;
  onClose: () => void;
}

const EXCLUDED_MEAL_NAMES = new Set(["lunch", "dinner"]);

function hasSelections(plan: DayPlan): boolean {
  if (!plan.meals) return false;
  return plan.meals.some((meal) =>
    meal.some((cg) => cg.selections.length > 0 || cg.skipped),
  );
}

function mealHasContent(meal: MealSelections | undefined): boolean {
  if (!meal) return false;
  return meal.some((cg) => cg.selections.length > 0 || cg.skipped);
}

function getMealItems(dayPlan: DayPlan, regimen: Regimen, mealIndex: number): string[] {
  const meal = regimen.meals[mealIndex]!;
  const mealSels = dayPlan.meals?.[mealIndex];
  if (!mealSels) return [];
  const menuId = dayPlan.mealMenus?.[mealIndex];
  const choices = getMealChoices(meal, menuId);
  const menu = getMealMenu(meal, menuId);
  const items: string[] = menu ? [menu.name] : [];
  for (let ci = 0; ci < choices.length; ci++) {
    const group = choices[ci]!;
    const cgSels = mealSels[ci];
    if (!cgSels || cgSels.skipped) continue;
    for (const sel of cgSels.selections) {
      const opt = group.options.find((o) => o.id === sel.optionId);
      if (!opt) continue;
      items.push(sel.count > 1 ? `${opt.name} x${sel.count}` : opt.name);
    }
  }
  return items;
}

interface ComputedSelection {
  day: DayOfWeek;
  dayLabel: string;
  regimen: Regimen;
  sourceDayPlan: DayPlan;
  mealIndices: number[];
  /** Meals from this day that already have content in the current week (will be replaced) */
  replacedMealLabels: string[];
}

export function DuplicatePreviousWeekDialog({
  currentWeekId,
  currentWeekPlan,
  onDuplicate,
  onClose,
}: DuplicatePreviousWeekDialogProps) {
  // Sources are the week owner's own weeks (a friend's in friend mode).
  const { owner } = useWeekPlan();
  const scope = useMemo(() => ownerScope(owner), [owner]);
  const previousWeekId = useMemo(() => addWeeks(currentWeekId, -1), [currentWeekId]);
  const [sourceWeekPlan, setSourceWeekPlan] = useState<WeekPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    fetchWeekSnapshot(scope, previousWeekId)
      .then((snapshot) => snapshot.plan)
      .then((plan) => {
        if (cancelled) return;
        if (!plan) {
          setSourceWeekPlan(null);
          setNotFound(true);
          setLoading(false);
          return;
        }
        const { plan: migrated } = migrateWeekPlan(plan);
        setSourceWeekPlan(migrated);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setSourceWeekPlan(null);
        setNotFound(true);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [previousWeekId, scope]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const computed: ComputedSelection[] = useMemo(() => {
    if (!sourceWeekPlan) return [];
    const out: ComputedSelection[] = [];
    for (const { id, label } of DAYS_OF_WEEK) {
      const sourceDayPlan = sourceWeekPlan[id];
      const targetDayPlan = currentWeekPlan[id];

      if (!sourceDayPlan?.regimenId) continue;
      if (sourceDayPlan.regimenId !== targetDayPlan?.regimenId) continue;
      if (!hasSelections(sourceDayPlan)) continue;

      const regimen = findRegimen(sourceDayPlan.regimenId);
      if (!regimen) continue;

      const mealIndices: number[] = [];
      const replacedMealLabels: string[] = [];
      regimen.meals.forEach((meal, mi) => {
        if (EXCLUDED_MEAL_NAMES.has(meal.name)) return;
        const sourceMeal = sourceDayPlan.meals?.[mi];
        if (!mealHasContent(sourceMeal)) return;
        mealIndices.push(mi);
        const targetMeal = targetDayPlan.meals?.[mi];
        if (mealHasContent(targetMeal)) {
          replacedMealLabels.push(meal.label);
        }
      });

      if (mealIndices.length === 0) continue;
      out.push({
        day: id,
        dayLabel: label,
        regimen,
        sourceDayPlan,
        mealIndices,
        replacedMealLabels,
      });
    }
    return out;
  }, [sourceWeekPlan, currentWeekPlan]);

  const totalMeals = computed.reduce((sum, c) => sum + c.mealIndices.length, 0);
  const replacements = computed.filter((c) => c.replacedMealLabels.length > 0);

  function handleDuplicate() {
    if (computed.length === 0) return;
    setShowConfirm(true);
  }

  function confirmDuplicate() {
    const selections: Record<string, { sourcePlan: DayPlan; mealIndices: number[] } | null> = {};
    for (const { id } of DAYS_OF_WEEK) {
      const match = computed.find((c) => c.day === id);
      selections[id] = match
        ? { sourcePlan: match.sourceDayPlan, mealIndices: match.mealIndices }
        : null;
    }
    onDuplicate(
      selections as Record<DayOfWeek, { sourcePlan: DayPlan; mealIndices: number[] } | null>,
    );
  }

  const hasReplacements = replacements.length > 0;
  const eatenWarning = eatenLossWarning(
    owner,
    computed.some((c) => hasEatenMarks(currentWeekPlan[c.day].meals, c.mealIndices)),
  );
  const confirmDescription = hasReplacements
    ? "Se reemplazarán las comidas existentes en:"
    : undefined;
  const confirmBody = hasReplacements ? (
    <ul className="mt-2 space-y-1">
      {replacements.map((c) => (
        <li key={c.day} className="flex gap-2">
          <span className="font-semibold text-foreground shrink-0">
            {c.dayLabel}
          </span>
          <span>{c.replacedMealLabels.join(", ")}</span>
        </li>
      ))}
    </ul>
  ) : undefined;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative bg-background rounded-t-2xl sm:rounded-2xl w-full max-w-md mx-auto p-5 space-y-4 safe-area-bottom max-h-[85dvh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h3 className="font-semibold text-base">
            Duplicar semana anterior menos almuerzo y cena
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            Semana origen: {formatWeekRange(previousWeekId)}
          </p>
        </div>

        {loading && <p className="text-sm text-muted-foreground">Cargando...</p>}

        {!loading && notFound && (
          <p className="text-sm text-muted-foreground">
            No hay plan guardado para la semana anterior.
          </p>
        )}

        {!loading && !notFound && computed.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No hay comidas que duplicar (la semana anterior no tiene desayunos, medias mañanas, medias tardes o snacks que coincidan con tu régimen actual).
          </p>
        )}

        {!loading && computed.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground">
              Vista previa ({totalMeals} {totalMeals === 1 ? "comida" : "comidas"})
            </p>
            <div className="space-y-1">
              {computed.map((c) => (
                <div
                  key={c.day}
                  className="border rounded-md px-2 py-1.5 text-xs"
                >
                  <div className="flex items-center gap-1.5">
                    <span className="font-semibold">{c.dayLabel}</span>
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                      {regimenBadge(c.regimen.id)}
                    </span>
                    <span className="text-[10px] text-muted-foreground ml-auto">
                      {c.mealIndices.length}{" "}
                      {c.mealIndices.length === 1 ? "comida" : "comidas"}
                    </span>
                  </div>
                  <div className="mt-0.5 space-y-0.5">
                    {c.mealIndices.map((mi) => {
                      const meal = c.regimen.meals[mi]!;
                      const items = getMealItems(c.sourceDayPlan, c.regimen, mi);
                      const note = c.sourceDayPlan.mealNotes?.[mi];
                      return (
                        <div key={mi} className="flex gap-1.5">
                          <span className="font-medium shrink-0">
                            {meal.label}
                            {note && (
                              <span className="font-normal text-muted-foreground italic ml-1">
                                ({note})
                              </span>
                            )}
                            :
                          </span>
                          <span className="text-muted-foreground truncate">
                            {items.length > 0 ? items.join(", ") : "—"}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <button
            onClick={handleDuplicate}
            disabled={computed.length === 0}
            className="flex-1 text-sm font-medium py-2 rounded-md bg-primary text-primary-foreground disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Duplicar {totalMeals > 0 ? `(${totalMeals})` : ""}
          </button>
          <button
            onClick={onClose}
            className="text-sm font-medium py-2 px-4 rounded-md border hover:bg-muted transition-colors"
          >
            Cancelar
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={showConfirm}
        onOpenChange={setShowConfirm}
        title="¿Duplicar comidas seleccionadas?"
        description={confirmDescription}
        body={
          eatenWarning ? (
            <>
              {confirmBody}
              <p className="mt-2">{eatenWarning}</p>
            </>
          ) : (
            confirmBody
          )
        }
        confirmText="Duplicar"
        destructive={hasReplacements}
        onConfirm={confirmDuplicate}
      />
    </div>
  );
}
